import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppSelector } from 'hooks';
import { isArray, isBoolean, isNull } from 'lodash';
import {
  useFetchQuestionsQuery,
  useGetCarDetailInfoQuery,
  useGetProvincesQuery,
  useLazyGetCitiesQuery,
  useSubmitAnswersMutation,
} from 'services/modules/carDetail';
import {
  onToggleIsShowError,
  onToggleIsValid,
  reset,
  setAnswers,
  setErrorsArray,
} from 'store/carDetail';
import { toggleBackButtonState } from 'store/common';
import { Button, Footer, Header, LoadingLottie } from 'ui';
import { Modal } from 'ui/Modal';

import { routes } from 'utils/routeConfig';

import {
  ICarDetail,
  IFormCarDetail,
  TPropertyValue,
  TStep,
} from '../../models/response/CarDetailResponse';
import { ICarDetailMeta, IDetailRequest } from '../../models/response/InspectionResponse';

import { ICarDetailsCard } from './CarDetailCard/CarDetailCard';
import CarDetailCard from './CarDetailCard';
import * as S from './CarDetailElements';

export const CarDetail = () => {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { backButtonState } = useAppSelector((state) => state.common);

  const {
    answers,
    elementError,
    isValid: isValidStore,
  } = useAppSelector((state) => state.carDetail);
  const { provinces } = useGetProvincesQuery(undefined, {
    selectFromResult: ({ data, isLoading }) => ({
      provinces: data?.results.map((item) => {
        return { title: item.name, id: item.id };
      }),
      provinceLoading: isLoading,
    }),
  });
  const [fetchCities, { data: cities, isFetching: cityLoading }] = useLazyGetCitiesQuery(undefined);

  useEffect(() => {
    (async () => {
      if (provinces) {
        const selectedProvince = provinces.find(
          (item) => item.title === answers?.vehicle_location?.province,
        );
        if (selectedProvince || answers?.vehicle_location?.province_id) {
          await fetchCities({
            provinceId: selectedProvince
              ? +selectedProvince.id
              : Number(answers?.vehicle_location?.province_id),
          });
        } else {
          await fetchCities({ provinceId: +provinces[0].id });
        }
      }
    })();
  }, [
    provinces?.length,
    answers?.vehicle_location?.province,
    answers?.vehicle_location?.province_id,
  ]);
  const citiesList = useMemo(() => {
    return cities?.results.map((item) => {
      return { title: item.name, id: item.id };
    });
  }, [cities, answers?.vehicle_location?.province]);

  const { currentCar } = useAppSelector((state) => state.inspection);
  const { data: dataQuestionsApi, isFetching: questionsFetching } = useFetchQuestionsQuery();
  const dataQuestions: ICarDetail[] | false =
    (isArray(dataQuestionsApi) &&
      dataQuestionsApi?.slice().sort((a: ICarDetail, b: ICarDetail) => a.order - b.order)) ||
    [];
  const [submit, { isLoading: submitIsLoading }] = useSubmitAnswersMutation();
  const { data: dataCarDetailInfo } = useGetCarDetailInfoQuery();

  useEffect(() => {
    return () => {
      dispatch(toggleBackButtonState('allow'));
    };
  }, []);

  useEffect(() => {
    if (currentCar) {
      let updateAnswers = { ...currentCar?.car_detail };
      if (
        isNull(updateAnswers['vehicle_owner_family_member_or_friend']) &&
        answers?.vehicle_owner_family_member_or_friend
      ) {
        updateAnswers['vehicle_owner_family_member_or_friend'] =
          answers?.vehicle_owner_family_member_or_friend;
      }

      const filteredAnswers: Partial<IDetailRequest> = {};

      for (const key in updateAnswers) {
        if (updateAnswers[key as keyof IDetailRequest] !== null) {
          // @ts-ignore
          filteredAnswers[key] = updateAnswers[key as keyof IDetailRequest];
        }
      }
      dispatch(setAnswers(filteredAnswers));
    }
  }, [currentCar]);
  const refs = useRef({});

  const dataApi: ICarDetailsCard.IDataApi = {
    ...dataCarDetailInfo,
    provinces: provinces || [],
    cities: citiesList || [],
  };
  const checkRequired = useCallback(() => {
    const errorList: TStep[] = [];
    isArray(dataQuestions) &&
      dataQuestions?.forEach((item) => {
        const currentStep = item?.step;
        const renderForm = (form: IFormCarDetail[], property_value: TPropertyValue | undefined) =>
          form.map((itemForm) => {
            const typeItemForm = itemForm.type.toUpperCase();
            let isEmpty = false;
            if (typeItemForm === 'TEXT' || typeItemForm === 'NUMBER' || typeItemForm === 'DATE') {
              const value =
                (property_value &&
                  // @ts-ignore
                  answers[property_value as TPropertyValue][itemForm.name as string]) ||
                answers[itemForm.name as keyof IDetailRequest];

              if (value === null || value === undefined || value === '' || value === 0) {
                if (!(itemForm.name === 'mileage' && value === 0)) {
                  isEmpty = true;
                }
              }
            } else if (typeItemForm === 'DROPDOWN' || typeItemForm === 'RADIO') {
              const options = itemForm.options
                ? itemForm.options
                : dataApi[itemForm.key_list_api as string]
                ? dataApi[itemForm.key_list_api]
                : itemForm.options;

              const value = answers[itemForm.name as keyof IDetailRequest];

              const isValue = options?.some((optionItem) => optionItem.value === value);

              const isRenderForm = options?.some(
                (optionItem) => optionItem?.is_render_form && optionItem.value === value,
              );
              if (isRenderForm && itemForm.render_form.form) {
                const { form, property_value } = itemForm.render_form;
                renderForm(form, property_value);
              } else if (!isValue) {
                isEmpty = true;
              }
            }
            if (isEmpty) {
              errorList.some((errorListItem) => errorListItem === currentStep) ||
                errorList.push(currentStep);
            }
          });

        renderForm(item?.form, item?.property_value);
      });

    dispatch(setErrorsArray(errorList));
    return errorList;
  }, [answers, dataApi]);
  const handleSubmit = async () => {
    if (!answers) return;
    let result = { ...answers };
    const slug = params.slug;

    if (answers.vehicle_owner) {
      result.vehicle_owner_family_member_or_friend = null;
      result.vehicle_owner_someone_else = null;
    }
    if (!answers.vehicle_owner && answers.vehicle_owner_someone_else !== 'familyOrFriend') {
      result.vehicle_owner_family_member_or_friend = null;
    }

    if (answers.on_finance === false) {
      result.balance_owed = null;
      result.balance = null;
    }

    if (isBoolean(answers.warranty) && !answers.warranty) {
      result.warranty_expire_date = null;
      result.warranty_expire_mileage = null;
    }

    if (answers.smc === false) {
      result.smc_form = null;
    }

    const errorList = checkRequired();
    const isValid = errorList.length === 0;
    if (slug) {
      const newCarDetail = JSON.parse(JSON.stringify(result), (key, value) =>
        value === '' ? null : value,
      );

      const body = {
        car_detail: {
          ...result,
          // @ts-ignore
          mileage: (newCarDetail.mileage && parseFloat(newCarDetail.mileage)) || 0,
        },
      };
      submit({
        body,
        slug,
      }).then(() => {});
    }
    if (!isValid) {
      dispatch(onToggleIsShowError(true));
      dispatch(onToggleIsValid(false));
      setTimeout(() => scrollToRef(errorList), 0);
    } else {
      navigate(-1);
    }
  };

  const scrollToRef = (errorList: string[]) => {
    if (isArray(errorList) && errorList?.length > 0) {
      // @ts-ignore
      refs[errorList[0]]?.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'start',
      });
    }
  };

  const handleCancel = () => {
    dispatch(toggleBackButtonState('prohibit'));
  };
  const handleClosePage = () => {
    const slug = params.slug;
    dispatch(toggleBackButtonState('allow'));
    dispatch(setAnswers(null));
    !!slug && navigate(routes.carSegments.replace(':slug', slug));
  };

  useEffect(() => {
    document
      .getElementsByTagName('body')[0]
      .classList.add(
        'tw-overflow-hidden',
        'tw-fixed',
        'tw-left-0',
        'tw-right-0',
        'tw-top-0',
        'tw-bottom-0',
      );
    return () => {
      dispatch(reset());
      document.getElementsByTagName('body')[0].className = '';
    };
  }, []);

  return (
    <S.Container>
      <Header
        title={
          currentCar
            ? `${currentCar?.model_full_name.make} ${currentCar?.model_full_name.model} ${currentCar?.model_full_name.year}`
            : ''
        }
        hasBack
      />
      <S.Content>
        <S.StatusBar>
          <S.StatusLabel>{t('car_inspection.car_information')}</S.StatusLabel>
          {currentCar && (
            <S.StatusLabel>{`${t('inspection.car_number')}: ${
              currentCar?.car_number
            }`}</S.StatusLabel>
          )}
        </S.StatusBar>
        {questionsFetching ? (
          <LoadingLottie />
        ) : (
          <S.CarList
            className={`tw-grid tw-grid-cols-1 car-list-with-error ${
              (elementError && elementError.length > 0 && 'car-list-with-error') || ''
            }`}
          >
            {dataCarDetailInfo &&
              isArray(dataQuestions) &&
              dataQuestions
                ?.sort((a: ICarDetail, b: ICarDetail) => a.order - b.order)
                .map((stepItem: ICarDetail) => {
                  return (
                    <CarDetailCard
                      key={stepItem.step}
                      valueMetaData={
                        stepItem.value_meta_data &&
                        (currentCar?.car_detail_meta[
                          stepItem.value_meta_data as keyof ICarDetailMeta
                        ] as string)
                      }
                      ref={(ref) => Object.assign(refs, { [stepItem.step]: ref })}
                      {...stepItem}
                      dataApi={dataApi}
                      step={stepItem.step}
                    />
                  );
                })}
          </S.CarList>
        )}
      </S.Content>
      <Modal
        title={t('car_inspection.are_you_sure')}
        onCloseModal={() => {
          dispatch(toggleBackButtonState('prohibit'));
        }}
        isShow={backButtonState === 'showErrorModal'}
      >
        <S.ErrorBodyWrapper>
          <p>{t('car_inspection.you_have_changed_the_form')}</p>
        </S.ErrorBodyWrapper>
        <S.ErrorButtons>
          <Button fullWidth onClick={handleClosePage}>
            {t('car_inspection.yes')}
          </Button>
          <Button fullWidth color="red" onClick={handleCancel}>
            {t('car_inspection.cancel')}
          </Button>
        </S.ErrorButtons>
      </Modal>
      <Footer slug={params.slug} isForceGetDetail>
        <Button size="large" onClick={handleSubmit} isLoading={submitIsLoading}>
          {t('car_detail.submit')}
        </Button>
      </Footer>
    </S.Container>
  );
};
